import { useEffect, useRef, useState } from 'react'

/**
 * Custom hook to detect and handle loading transitions to prevent "Not Used" label flashing
 *
 * @param isLoading - Current loading state
 * @param contentLength - Current number of content items (e.g., transformedNodes.length)
 * @param maxTransitionTime - Maximum time to wait for content before showing "Not Used" (default: 5000ms)
 * @returns Whether to show loading state to prevent "Not Used" flash
 */
export const useLoadingTransition = (
    isLoading: boolean,
    contentLength: number,
    maxTransitionTime: number = 5000
): boolean => {
    // Track previous state
    const prevContentLength = useRef(0)
    const prevLoading = useRef(false)
    const timeoutId = useRef<NodeJS.Timeout | null>(null)

    // Detect if we're in a transition state
    const [isInTransition, setIsInTransition] = useState(false)

    /**
     * Helper function to determine if we should be in transition state
     * Extracted to avoid duplication between useEffect and return statement
     *
     * Transition occurs when:
     * - We had content or were loading previously
     * - Now we have no content (only header node)
     * - And we're not currently loading
     */
    const computeShouldTransition = () => {
        const wasLoadingOrHadContent =
            prevLoading.current || prevContentLength.current > 1
        const nowEmpty = contentLength <= 1 // Account for header node (always 1)
        return wasLoadingOrHadContent && nowEmpty && !isLoading
    }

    useEffect(() => {
        // Clear any existing timeout
        if (timeoutId.current) {
            clearTimeout(timeoutId.current)
            timeoutId.current = null
        }

        const shouldTransition = computeShouldTransition()

        if (shouldTransition) {
            setIsInTransition(true)

            // Set timeout to end transition
            timeoutId.current = setTimeout(() => {
                setIsInTransition(false)
                timeoutId.current = null
            }, maxTransitionTime)
        } else if (!shouldTransition) {
            // End transition immediately if conditions no longer met
            setIsInTransition(false)
        }

        // Update previous state refs for next render
        prevContentLength.current = contentLength
        prevLoading.current = Boolean(isLoading)

        // Cleanup on unmount
        return () => {
            if (timeoutId.current) {
                clearTimeout(timeoutId.current)
                timeoutId.current = null
            }
        }
    }, [contentLength, isLoading, maxTransitionTime])

    // Return whether to show loading state
    return Boolean(isLoading) || isInTransition || computeShouldTransition()
}
